// apps/radar/src/routes/leads.tsx
// Version: 2026.4.24

import { createFileRoute } from '@tanstack/react-router';
import {
  EnginePageShell, GlassPanel, SectionLabel, EmptyState, LoadingShimmer,
  StatusBadge, C, F,
} from '../components/engine';
import { useLeads } from '../lib/api/hooks';
import type { LeadTier } from '@forgetrack/api-client';

export const Route = createFileRoute('/leads')({ component: LeadsPage });

const TIER_COLOR: Record<LeadTier, string> = {
  hot: '#f97066',  warm: '#fbbf24',  cold: '#60a5fa',
};

function LeadsPage() {
  const { data, loading, error } = useLeads();

  return (
    <EnginePageShell
      title="Leads"
      subtitle="INTENT INBOX"
      icon="🎯"
      accentColor={C.cyan}
      brand="FORGERADAR"
      footer="ForgeRadar · Edson Technologies · forgeradar.app"
    >
      <GlassPanel accent={C.cyan}>
        <SectionLabel color={C.cyan}>Lead Inbox</SectionLabel>

        {error && (
          <div style={{ padding: '24px 0', color: C.coral }}>
            Failed to load leads: {error.message}
          </div>
        )}

        {!error && loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 0' }}>
            {Array.from({ length: 5 }).map((_, i) => <LoadingShimmer key={i} height={28} />)}
          </div>
        )}

        {!error && !loading && (data?.length ?? 0) === 0 && (
          <EmptyState
            icon="🎯"
            title="No leads classified yet"
            subtitle="The inbox lights up once the classifier (Phase 1 Sprint 5) produces its first scored artifacts."
          />
        )}

        {!error && !loading && data && data.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{
                textAlign: 'left', color: C.muted, fontSize: 11,
                fontFamily: F.mono, textTransform: 'uppercase', letterSpacing: '0.12em',
              }}>
                <th style={th}>Tier</th>
                <th style={th}>Title</th>
                <th style={th}>Source</th>
                <th style={th}>Category</th>
                <th style={{ ...th, textAlign: 'right' }}>Score</th>
                <th style={th}>Captured</th>
              </tr>
            </thead>
            <tbody>
              {data.map(lead => (
                <tr key={lead.leadId} style={{ fontSize: 13, color: C.text }}>
                  <td style={td}>
                    <StatusBadge label={lead.tier} color={TIER_COLOR[lead.tier]} glow={lead.tier === 'hot'} />
                  </td>
                  <td style={td}>{lead.title}</td>
                  <td style={{ ...td, color: C.muted, fontFamily: F.mono, fontSize: 12 }}>{lead.sourceCode}</td>
                  <td style={{ ...td, color: C.muted, fontFamily: F.mono, fontSize: 12 }}>{lead.category}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: F.mono, color: TIER_COLOR[lead.tier] }}>
                    {lead.score}
                  </td>
                  <td style={{ ...td, color: C.dim, fontFamily: F.mono, fontSize: 11 }}>
                    {new Date(lead.capturedAt).toISOString().slice(0, 16).replace('T', ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassPanel>
    </EnginePageShell>
  );
}

const th = { padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.18)' };
const td = { padding: '12px 8px' };
